/**
 * CallLogsReportPage — V2 orchestrator for the Call Logs report.
 *
 * Brand-neutral name (currently Grandstream-backed, gated on is_grandstream
 * at the route + nav level). Mirrors AlertDetailsPage closely:
 *   - Time badge + popover (quick ranges; "Custom Range…" opens the drawer)
 *   - A "Report Settings" drawer holding Time Range + the Call Types filter
 *   - Summary ↔ Detailed view toggle
 * The backend returns only `records`; the summary is computed client-side.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import {
    iAmHigherAuthority,
    formatDateIso,
    formatDateTime12Hour,
    getToday,
    getWeekRange,
    getMonthRange,
    getQuarterRange,
} from '../../../libs.jsx';
import { fetchCallLogsReport, fetchCallTypes, fetchFilterOptions } from './api/callLogsReportApi.js';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import CallLogsSummary from './components/CallLogsSummary.jsx';
import CallLogsReportTable from './components/CallLogsReportTable.jsx';
import CallLogsSettingsDrawer from './components/CallLogsSettingsDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';

import './call-logs-report-page.css';

// ---------------------------------------------------------------------------
// Time range presets (mirrors HourlySummaryPage / AlertDetailsPage)
// ---------------------------------------------------------------------------
const TIME_RANGES = [
    { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0,0,0,0); const e = new Date(); return [d, e]; } },
    { id: 2, label: 'Yesterday', getRange: () => { const d = getToday(-1); d.setHours(0,0,0,0); const e = getToday(-1); e.setHours(23,59,59,999); return [d, e]; } },
    { id: 3, label: 'This Week', getRange: () => { const [s] = getWeekRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 4, label: 'This Month', getRange: () => { const [s] = getMonthRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 5, label: 'This Quarter', getRange: () => { const [s] = getQuarterRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 6, label: 'Last Week', getRange: () => { const [s, e] = getWeekRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 7, label: 'Last Month', getRange: () => { const [s, e] = getMonthRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 8, label: 'Last Quarter', getRange: () => { const [s, e] = getQuarterRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
];

const RANGE_NAMES = { 1: 'Today', 2: 'Yesterday', 3: 'This Week', 4: 'This Month', 5: 'This Quarter', 6: 'Last Week', 7: 'Last Month', 8: 'Last Quarter' };

const CallLogsReportPage = () => {
    // Navigation state from ScheduledReportsPage "Run Report Local" — pre-fills
    // the time range + call-type filter and auto-runs the report on mount.
    const location = useLocation();
    const navStateRef = useRef(location.state);
    const navState = navStateRef.current;

    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session]
    );

    // -- Report state --
    const [status, setStatus] = useState('init'); // 'init' | 'loading' | 'ready' | 'error'
    const [records, setRecords] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');
    const [viewMode, setViewMode] = useState('summary'); // 'summary' | 'detailed'

    // -- Time range -- (seeded from nav state when launched via Run Report Local)
    const navRangeId = navState?.rangeId && RANGE_NAMES[navState.rangeId] ? navState.rangeId : 2;
    const [rangeId, setRangeId] = useState(navRangeId);
    const [rangeName, setRangeName] = useState(RANGE_NAMES[navRangeId] || 'Yesterday');
    const [customRange, setCustomRange] = useState(false);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) { const d = new Date(navState.startDate); if (!Number.isNaN(d.getTime())) return d; }
        const d = getToday(-1); d.setHours(0,0,0,0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) { const d = new Date(navState.endDate); if (!Number.isNaN(d.getTime())) return d; }
        const d = getToday(-1); d.setHours(23,59,59,999); return d;
    });

    // -- Time popover --
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);

    // -- Settings drawer --
    const [settingsOpen, setSettingsOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);
    const [filterOptions, setFilterOptions] = useState({}); // for the schedule user-recipient picker
    const [saving, setSaving] = useState(false);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((message, type = 'success') => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 5000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // -- Call-type filter -- (seeded from nav state's callTypes when present)
    const [callTypes, setCallTypes] = useState([]);          // [{ call_type_id, call_type_name }]
    const [selectedCallTypes, setSelectedCallTypes] = useState(
        () => (navState?.filters?.callTypes || []).map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n))
    ); // number[]

    // Re-run after a settings apply/clear if the report was already run
    const shouldRerunRef = useRef(false);

    // Load call-type options once
    useEffect(() => {
        let cancelled = false;
        fetchCallTypes()
            .then((data) => { if (!cancelled) setCallTypes(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setCallTypes([]); });
        return () => { cancelled = true; };
    }, []);

    // Load frequencies + filter options (campus users for the recipient picker)
    // for the Schedule Report drawer.
    useEffect(() => {
        let cancelled = false;
        fetchFrequencies()
            .then((data) => { if (!cancelled) setFrequencies(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setFrequencies([]); });
        fetchFilterOptions(campusId)
            .then((opts) => { if (!cancelled) setFilterOptions(opts || {}); })
            .catch(() => { if (!cancelled) setFilterOptions({}); });
        return () => { cancelled = true; };
    }, [campusId]);

    // Merge call types into the schedule drawer's options under `callTypes` so
    // its filter-term builder can resolve call_type_id → name labels.
    const scheduleFilterOptions = useMemo(
        () => ({ ...filterOptions, callTypes }),
        [filterOptions, callTypes]
    );

    // Close time popover on outside click
    useEffect(() => {
        if (!timePopoverOpen) return;
        const onDocClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [timePopoverOpen]);

    // ----- Time handlers -----
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    /** Custom Range… in the popover opens the Report Settings drawer. */
    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setSettingsOpen(true);
    }, [handleCustomToggle]);

    // ----- Run report -----
    const handleRunReport = useCallback(async () => {
        setStatus('loading');
        setErrorMsg('');
        const result = await fetchCallLogsReport({
            campusId,
            startDate: formatDateIso(start),
            endDate: formatDateIso(end),
            callTypeFilter: selectedCallTypes.join(','),
        });
        if (result?.ErrorCode) {
            setRecords([]);
            setStatus('error');
            setErrorMsg(
                result.ErrorCode === 11 ? 'The report service is temporarily unavailable. Please try again.'
                    : result.ErrorCode === 12 ? 'You are not authorized to run this report.'
                    : 'Something went wrong generating the report. Please try again.'
            );
            return;
        }
        setRecords(result.records || []);
        setStatus('ready');
    }, [campusId, start, end, selectedCallTypes]);

    // Re-run when a settings apply/clear changed the selection (only if already run)
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [selectedCallTypes, handleRunReport]);

    // Auto-run on mount when launched via "Run Report Local" from the
    // scheduled reports page (state.autoRun). Time range + call types are
    // already seeded into state above, so the initial run uses them.
    const autoRunRef = useRef(Boolean(navState?.autoRun));
    useEffect(() => {
        if (autoRunRef.current) {
            autoRunRef.current = false;
            handleRunReport();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ----- Settings drawer apply/clear -----
    const handleApplySettings = useCallback((nextCallTypes) => {
        setSelectedCallTypes(nextCallTypes);
        setSettingsOpen(false);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    const handleClearSettings = useCallback(() => {
        setSelectedCallTypes([]);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    // ----- Schedule report -----
    const handleScheduleSave = useCallback(async (payload) => {
        setSaving(true);
        const result = await createSchedule(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);
        if (result[0]) {
            showToast('Schedule created successfully.');
            setScheduleOpen(false);
        } else {
            showToast(result[1] || 'Failed to create schedule.', 'error');
        }
    }, [campusId, showToast]);

    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setStatus('init');
        setRecords([]);
    }, []);

    const activeFilterCount = selectedCallTypes.length;

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">

                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Call Logs</span>
                                </div>

                                <div className="ar-campus-row">
                                    <label htmlFor="cl-campus-selector" className="ar-campus-label">Campus:</label>
                                    {showCampusSelector && campusList.length > 1 ? (
                                        <select id="cl-campus-selector" value={campusId} onChange={handleCampusChange} className="ar-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Call Logs</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    {/* Time range badge + popover */}
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Schedule Report — static custom dates aren't eligible
                                        for recurring schedules, so it's disabled in custom mode. */}
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>

                                    {/* Report Settings (time + call types) */}
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setSettingsOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeFilterCount}</span>
                                        ) : null}
                                    </button>

                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle */}
                    {status === 'ready' ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`} onClick={() => setViewMode('summary')}>Summary</button>
                                <button className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`} onClick={() => setViewMode('detailed')}>Detailed</button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-phone-call" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Select a time range and click <strong>Run Report</strong> to generate the call logs report.</p>
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={handleRunReport} style={{ marginTop: 'var(--space-3)' }}>
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />Run Report
                                </button>
                            </div>
                        ) : null}

                        {status === 'loading' ? (
                            <div className="ar-loading">
                                <div className="ar-loading__spinner" />
                                <p>Loading call logs...</p>
                            </div>
                        ) : null}

                        {status === 'error' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-alert-triangle" style={{ fontSize: '40px', color: 'var(--color-warning)', marginBottom: 'var(--space-4)' }} />
                                <p>{errorMsg}</p>
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={handleRunReport} style={{ marginTop: 'var(--space-3)' }}>
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />Try Again
                                </button>
                            </div>
                        ) : null}

                        {status === 'ready' ? (
                            viewMode === 'summary'
                                ? <CallLogsSummary records={records} />
                                : <CallLogsReportTable
                                    records={records}
                                    tzOffset={tzOffset}
                                    dateRangeLabel={customRange
                                        ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                        : rangeName}
                                  />
                        ) : null}
                    </div>

                    {/* Report Settings drawer */}
                    <CallLogsSettingsDrawer
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                        onApply={handleApplySettings}
                        onClear={handleClearSettings}
                        saving={status === 'loading'}
                        callTypes={callTypes}
                        selectedCallTypes={selectedCallTypes}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={(date) => setStart(date)}
                        onEndChange={(date) => setEnd(date)}
                    />

                    {/* Schedule Report drawer (report_type_id 14) */}
                    <ScheduleReportDrawer
                        open={scheduleOpen}
                        onClose={() => setScheduleOpen(false)}
                        onSave={handleScheduleSave}
                        saving={saving}
                        reportTypeId={14}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={{ callTypes: selectedCallTypes }}
                        filterOptions={scheduleFilterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default CallLogsReportPage;
